// Pure date/grouping helpers for the Recurring Invoices "Timeline" view.
//
// All helpers work off the DATE part (YYYY-MM-DD) of an ISO-8601 string rather
// than `new Date(iso)`, on purpose: the scheduled dates come from crm-api at
// midnight UTC, and parsing them into a local Date can shift the calendar day
// (and therefore the MONTH bucket) for viewers west of UTC. Slicing the date
// prefix keeps grouping + labels stable regardless of the viewer's timezone.

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export interface IsoDateParts {
  y: number
  m: number // 1-12
  d: number // 1-31
}

/** Split the YYYY-MM-DD prefix of an ISO string into numeric parts. */
export function isoDateParts(iso: string): IsoDateParts {
  const [y, m, d] = iso.slice(0, 10).split('-').map((n) => Number(n))
  return { y: y ?? 0, m: m ?? 1, d: d ?? 1 }
}

/** Stable month bucket key, e.g. "2026-07". */
export function monthKey(iso: string): string {
  const { y, m } = isoDateParts(iso)
  return `${y}-${String(m).padStart(2, '0')}`
}

/** Human month header, e.g. "July 2026". */
export function monthLabel(iso: string): string {
  const { y, m } = isoDateParts(iso)
  return `${MONTH_NAMES[m - 1] ?? ''} ${y}`
}

/** Day-of-month for the date badge, e.g. 15. */
export function dayOfMonth(iso: string): number {
  return isoDateParts(iso).d
}

/** Uppercase 3-letter month for the date badge, e.g. "JUL". */
export function monthAbbrUpper(iso: string): string {
  return (MONTH_ABBR[isoDateParts(iso).m - 1] ?? '').toUpperCase()
}

/** Compact date, e.g. "Aug 14, 2026". */
export function formatDateShort(iso: string): string {
  const { y, m, d } = isoDateParts(iso)
  return `${MONTH_ABBR[m - 1] ?? ''} ${d}, ${y}`
}

/**
 * Whole calendar days from `now` until the target date (negative if past).
 * Uses UTC-normalized day boundaries so DST transitions can't introduce an
 * off-by-one.
 */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const { y, m, d } = isoDateParts(iso)
  const target = Date.UTC(y, m - 1, d)
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((target - today) / 86_400_000)
}

/** "today" / "tomorrow" / "in N days" / "N days ago". */
export function relativeDayLabel(iso: string, now: Date = new Date()): string {
  const n = daysUntil(iso, now)
  if (n === 0) return 'today'
  if (n < 0) return `${Math.abs(n)} day${n === -1 ? '' : 's'} ago`
  if (n === 1) return 'tomorrow'
  return `in ${n} days`
}

export interface TimelineMonthGroup<T> {
  key: string
  label: string
  items: T[]
}

/**
 * Bucket occurrences into consecutive month groups, PRESERVING input order
 * (the projection API already returns them ascending by scheduled_date, so
 * both the groups and the items within them stay chronological).
 */
export function groupByMonth<T extends { scheduled_date: string }>(
  items: T[],
): TimelineMonthGroup<T>[] {
  const groups: TimelineMonthGroup<T>[] = []
  const byKey = new Map<string, TimelineMonthGroup<T>>()
  for (const item of items) {
    const key = monthKey(item.scheduled_date)
    let group = byKey.get(key)
    if (!group) {
      group = { key, label: monthLabel(item.scheduled_date), items: [] }
      byKey.set(key, group)
      groups.push(group)
    }
    group.items.push(item)
  }
  return groups
}
